import * as Sentry from '@sentry/browser';
import { createBrowserHistory } from 'history';

import { EventTrackingFn } from './declarations';
import './googleTagManager';
import { isQuotaExceededError } from './isQuotaExceededError';
import { getRailsEnv } from './railsEnv';
import segment from './segment';

/**
 * User-agent used by Invicti, the web scanner we use at OpenDoor to scan for security concerns
 * We do not want to track events from Invicti in journey as they are not true UserEvents
 * ip: 54.88.149.100
 */
const naivePattern =
  /bot|spider|crawl|http|lighthouse|.*Netsparker|.*ThousandEyes-Dragonfly-x1|X-Athena/i;
export const isbotNaive = (userAgent?: string | null): boolean =>
  Boolean(userAgent) && naivePattern.test(userAgent as string);

const LOG_LEVEL_CONFIG_NAME = 'opendoor_log_level';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const log = (..._args: any[]) => {
  if (
    typeof window !== 'undefined' &&
    window.localStorage.getItem(LOG_LEVEL_CONFIG_NAME) === 'true'
  ) {
    // eslint-disable-next-line no-console
    console.log(..._args);
  }
};

/* eslint-disable */
// Linter doesn't like the way we're just calling functions without defining them ourselves
declare var ga: any;
declare var window: any;

const wrapQuotaError = (cb: any) => {
  try {
    cb();
  } catch (e) {
    if (!isQuotaExceededError(e)) {
      Sentry.captureException(e);
    }
  }
};

export const register = (traits: any) => {
  if (typeof ga === 'function' && typeof traits === 'object') {
    ga('set', traits);
  }
  segment.identify(traits);
  if (getRailsEnv() !== 'production') {
    log(`[DEBUG] Analytics.register: \n${JSON.stringify(traits, null, 2)}`);
  }
};

// use this for logged on users, so we don't need to use ajs anonymous id
export const registerWithUserID = (userId: string, traits: any) => {
  if (typeof ga === 'function' && typeof traits === 'object') {
    ga('set', traits);
  }
  segment.identify(userId, traits);
  if (getRailsEnv() !== 'production') {
    log(`[DEBUG] Analytics.register: \n${userId} ${JSON.stringify(traits, null, 2)}`);
  }
};

export const trackEvent: EventTrackingFn = (category, action, value, extra) => {
  // Exit early if it's a bot. Don't waste our Segment MTU quota
  if (isbotNaive(navigator.userAgent)) {
    return;
  }

  // Have to copy-paste the try/catch because doing try/catch functionally
  // with generators is complicated
  wrapQuotaError(() => {
    // https://developers.google.com/analytics/devguides/collection/analyticsjs/events
    if (typeof ga === 'function') {
      ga('send', 'event', category, action, value, extra);
    }
  });

  wrapQuotaError(() => {
    // https://segment.com/docs/spec/track/
    segment.track(`${category}-${action}`, {
      channel: 'web',
      domain: window.location.host,
      screen: {
        height: window.screen.height,
        width: window.screen.width,
      },
      value,
      ...extra,
    });
  });

  wrapQuotaError(() => {
    // https://support.google.com/tagmanager/answer/6106961?hl=en
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: `${category}.${action}.${value}`, extra });
  });

  if (getRailsEnv() !== 'production') {
    log(
      `[DEBUG] Analytics.trackEvent: \n${category}-${action} ${JSON.stringify(
        {
          value,
          ...extra,
        },
        null,
        2,
      )}`,
    );
  }
};

const matchInAppQueryParam = (queryParams) => {
  const fragments = queryParams && queryParams.match(/app\=(\d+)/);
  if (!fragments) {
    return false;
  }
  return !!fragments[1];
};

export const trackPageView = (extra) => {
  const history = createBrowserHistory();
  const pathname = history.location.pathname;
  const search = history.location.search;
  trackEvent('page', 'view', 'page', {
    app: matchInAppQueryParam(search),
    pathname,
    search,
    ...extra,
  });
};
